import { Plus } from '@untitledui/icons';
import type { RefObject } from 'react';
import { useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { IconButton } from '../../../components/buttons/IconButton';
import DropDownMenu from '../../../components/general/DropDownMenu';
import { Text } from '../../../components/general/Text';
import { GCS_CONFIGS } from '../../../config/gcs/gcsConfigs';
import { useFocusMap } from '../../../hooks/useFocusMap';
import { en } from '../../../locales/en';
import { localStorageService } from '../../../services/storage/LocalStorage';
import { useSelectedGcsConfigsStore } from '../../../store/useSelectedGcsConfigsStore';

interface CentersPanelHeaderTileProps {
  mapRef: RefObject<MapRef | null>;
}

function CentersPanelHeaderTile({ mapRef }: CentersPanelHeaderTileProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { addGcsConfig } = useSelectedGcsConfigsStore();
  const focusMap = useFocusMap(mapRef);

  // Check if there are unselected centers available
  const selectedIds = localStorageService.getSelectedGcsIds() || [];
  const missingCenters = GCS_CONFIGS.filter((config) => !selectedIds.includes(config.id));
  const hasUnselectedCenters = missingCenters.length > 0;

  const onAddCenterClicked = (gcsId: string) => {
    addGcsConfig(gcsId, true);
    setIsDropdownOpen(false);
    requestAnimationFrame(() => {
      focusMap();
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        padding: '0px 8px'
      }}
    >
      <Text variant='h2'>{en.centersPanel.title}</Text>

      {hasUnselectedCenters && (
        <div style={{ position: 'relative' }}>
          <IconButton
            icon={Plus}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            title={en.centersPanel.addCenter}
          />

          {/* Dropdown Menu for Missing Centers */}
          <DropDownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)} top='100%' right='0'>
            {missingCenters.map((config, index) => (
              <button
                key={config.id}
                onClick={() => onAddCenterClicked(config.id)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderBottom: index < missingCenters.length - 1 ? '1px solid var(--color-divider)' : 'none'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Text variant='body2-medium'>{config.nameCode}</Text>
              </button>
            ))}
          </DropDownMenu>
        </div>
      )}
    </div>
  );
}

export default CentersPanelHeaderTile;
